import { useHistory, useRouteMatch } from "react-router-dom";
import {
  COIN_EXTRA_DETAILS_PATH,
  COIN_EXTRA_DETAILS_TABS,
  COIN_EXTRA_DETAILS_TABS_TO_DISPLAY,
  DRAWER_TRADES_TABLE_FULL_WIDTH,
  PORTFOLIO_POSITIONS_PATH,
} from "common/constants";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import React, { useEffect, useMemo, useState } from "react";
import AssetGeneralTab from "features/portfolio/CoinDrawer/AssetGeneralTab.tsx";
import DrawerTitleLayout from "common/Drawers/DrawerTitleLayout";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import DrawerTradesPage from "features/trades/common/DrawerTradesPage";
import AddToWatchlistModal from "features/watchlist/AllMarkets/AddToWatchlistModal";
import AntStarFilled from "@ant-design/icons/StarFilled";
import AntStarOutlined from "@ant-design/icons/StarOutlined";
import IconActionButton from "common/Buttons/IconActionButton";
import styled from "styled-components";
import useWatchlist from "features/watchlist/common/useWatchlist";
import DrawerAccountsTab from "features/portfolio/CoinDrawer/DrawerAccountsTab.tsx";
import { useDrawerDelayedUnmount } from "utils/hooks/misc.tsx";
import { skipToken } from "@reduxjs/toolkit/query";
import PositionGeneralTab from "features/portfolio/CoinDrawer/PositionGeneralTab.tsx";
import { getRouteWithoutPath } from "utils/routes.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useIsSubUser } from "@shared/utils/hooks/misc.ts";

const AssetDrawer = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const history = useHistory();
  const match = useRouteMatch(COIN_EXTRA_DETAILS_PATH);
  const matchParams = (match?.params || {}) as {
    coin: string;
    tab: string;
  };
  const coin = decodeURIComponent(matchParams.coin || "");
  const isPositions = !!useRouteMatch(PORTFOLIO_POSITIONS_PATH);
  const { id: drawerCoin, isUnmounting } = useDrawerDelayedUnmount(coin);
  const isAsset = !drawerCoin?.includes("/");
  const defaultTab = matchParams.tab || COIN_EXTRA_DETAILS_TABS.GENERAL;
  const [tab, setTab] = useState(defaultTab);
  const isTradesTab = tab === COIN_EXTRA_DETAILS_TABS.TRADES;
  const isDrawerOpen = !!drawerCoin && !isUnmounting;
  const args = useMemo(
    () => (drawerCoin ? { coin: drawerCoin } : skipToken),
    [drawerCoin]
  );
  const { getWatchlistLoading, getIsSymbolInWatchlist } = useWatchlist(
    !isDrawerOpen
  );
  const isSymbolInWatchlist = getIsSymbolInWatchlist(drawerCoin);
  const isSubUser = useIsSubUser();

  useEffect(() => {
    if (isDrawerOpen && defaultTab !== tab) {
      setTab(defaultTab);
    }
  }, [isDrawerOpen]);

  const [addToWatchlistModalVisible, setAddToWatchlistModalVisible] =
    useState(false);

  const onCloseDrawer = () => {
    history.push(
      getRouteWithoutPath(location.pathname, COIN_EXTRA_DETAILS_PATH)
    );
  };

  const tabToComponent: Record<string, React.JSX.Element> = {
    [COIN_EXTRA_DETAILS_TABS.GENERAL]: isPositions ? (
      <PositionGeneralTab />
    ) : (
      <AssetGeneralTab args={args} isDrawerOpen={isDrawerOpen} />
    ),
    [COIN_EXTRA_DETAILS_TABS.ACCOUNTS]: (
      <DrawerAccountsTab args={args} isDrawerOpen={isDrawerOpen} />
    ),
    [COIN_EXTRA_DETAILS_TABS.TRADES]: (
      <DrawerTradesPage
        baseGetTradesArgs={args}
        isDrawerOpen={isDrawerOpen}
        tabKey={COIN_EXTRA_DETAILS_TABS.TRADES}
      />
    ),
  };

  if (isPositions) {
    delete tabToComponent[COIN_EXTRA_DETAILS_TABS.ACCOUNTS];
  }

  const leftActions = !getWatchlistLoading && !isAsset && !isSubUser && (
    <IconActionButton
      icon={isSymbolInWatchlist ? <StarFilled /> : <StarOutlined />}
      onClick={() => setAddToWatchlistModalVisible(true)}
      actionName={
        isSymbolInWatchlist ? t("watchlist.update") : t("watchlist.add")
      }
    />
  );

  return (
    <>
      <ExtraDetailsDrawer
        path={COIN_EXTRA_DETAILS_PATH}
        visible={isDrawerOpen}
        tabsToComponents={tabToComponent}
        onCloseDrawer={onCloseDrawer}
        title={
          drawerCoin ? (
            <DrawerTitleLayout
              title={
                <TextWithIcon
                  text={drawerCoin}
                  coinOrExchangeName={drawerCoin}
                  iconType={ICON_TYPES.COIN}
                  justify={"center"}
                  style={{ width: "100%" }}
                />
              }
              leftActions={leftActions}
            />
          ) : (
            ""
          )
        }
        onTabChange={setTab}
        tab={tab}
        minWidth={isTradesTab ? DRAWER_TRADES_TABLE_FULL_WIDTH : undefined}
        flatListTabs={[
          COIN_EXTRA_DETAILS_TABS.ACCOUNTS,
          COIN_EXTRA_DETAILS_TABS.TRADES,
        ]}
        getTabLabel={(tab) => t(COIN_EXTRA_DETAILS_TABS_TO_DISPLAY[tab])}
      />
      <AddToWatchlistModal
        visible={addToWatchlistModalVisible}
        symbol={coin}
        onClose={() => setAddToWatchlistModalVisible(false)}
      />
    </>
  );
};

export default AssetDrawer;

const StarFilled = styled(AntStarFilled)`
  position: relative;
  top: 1px;
  svg {
    color: ${(p) => p.theme.colors.primaryBlue}!important;
  }
`;
const StarOutlined = styled(AntStarOutlined)`
  position: relative;
  top: 1px;
`;
